import * as React from 'react';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';

import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import AdminLayout from '../components/AdminLayout';
import Api from '../services/Api';
import { CancelOutlined, CardGiftcardOutlined, CheckCircleOutline, Inventory2Outlined, MonetizationOnOutlined } from '@mui/icons-material';

// -----------------------------
// DTOs / lookups
// -----------------------------

export interface IPrizeDto {
    id: number;
    tournamentId: number;
    miniGameId: number; // 0 o null si no aplica según implementación backend
    name: string | null;
    description: string | null;
    imageUrl: string | null;
    pointsCost: number;
    stock: number | null;
    isActive: boolean;
}

export interface ITournamentLookup {
    id: number;
    name: string | null;
}

export interface IMiniGameLookup {
    id: number;
    tournamentId: number;
    name: string | null;
}

// -----------------------------
// Diálogo alta / edición premio
// -----------------------------

interface IPrizeDialogProps {
    open: boolean;
    mode: 'create' | 'edit';
    prize: IPrizeDto | null;
    tournaments: ITournamentLookup[];
    miniGames: IMiniGameLookup[];
    filterTournamentId: number | null;
    filterMiniGameId: number | null;
    onClose: () => void;
    onSaved: () => void;
}

interface IPrizeDialogState {
    tournamentId: number | null;
    miniGameId: number | null;
    name: string;
    description: string;
    imageUrl: string;
    pointsCost: string;
    stock: string;
    isActive: boolean;
    saving: boolean;
    error: string | null;
}

class PrizeDialog extends React.Component<IPrizeDialogProps, IPrizeDialogState> {
    public constructor(props: IPrizeDialogProps) {
        super(props);

        const p = props.prize;

        const initialTournamentId =
            props.filterTournamentId ?? (p ? p.tournamentId : null);
        const initialMiniGameId =
            props.filterMiniGameId ??
            (p && p.miniGameId ? p.miniGameId : null);

        this.state = {
            tournamentId: initialTournamentId,
            miniGameId: initialMiniGameId,
            name: p?.name || '',
            description: p?.description || '',
            imageUrl: p?.imageUrl || '',
            pointsCost: p ? String(p.pointsCost) : '',
            stock: p && p.stock !== null ? String(p.stock) : '',
            isActive: p?.isActive ?? true,
            saving: false,
            error: null
        };
    }

    public componentDidUpdate(prevProps: IPrizeDialogProps): void {
        if (
            this.props.open &&
            (!prevProps.open ||
                prevProps.mode !== this.props.mode ||
                prevProps.prize?.id !== this.props.prize?.id ||
                prevProps.filterTournamentId !== this.props.filterTournamentId ||
                prevProps.filterMiniGameId !== this.props.filterMiniGameId)
        ) {
            const p = this.props.prize;
            const initialTournamentId =
                this.props.filterTournamentId ?? (p ? p.tournamentId : null);
            const initialMiniGameId =
                this.props.filterMiniGameId ??
                (p && p.miniGameId ? p.miniGameId : null);

            this.setState({
                tournamentId: initialTournamentId,
                miniGameId: initialMiniGameId,
                name: p?.name || '',
                description: p?.description || '',
                imageUrl: p?.imageUrl || '',
                pointsCost: p ? String(p.pointsCost) : '',
                stock: p && p.stock !== null ? String(p.stock) : '',
                isActive: p?.isActive ?? true,
                saving: false,
                error: null
            });
        }
    }

    private validate(): string | null {
        const { tournamentId, name, pointsCost } = this.state;
        if (!tournamentId) return 'El torneo es obligatorio.';
        if (!name.trim()) return 'El nombre es obligatorio.';
        if (!pointsCost.trim()) return 'El coste en puntos es obligatorio.';

        const points = Number(pointsCost);
        if (Number.isNaN(points) || points < 0) {
            return 'El coste en puntos debe ser un número mayor o igual que 0.';
        }

        if (this.state.stock.trim()) {
            const stockVal = Number(this.state.stock);
            if (Number.isNaN(stockVal) || stockVal < 0) {
                return 'El stock debe ser un número mayor o igual que 0.';
            }
        }

        return null;
    }

    private handleTournamentChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        this.setState({
            tournamentId: value ? Number(value) : null,
            // si cambiamos de torneo, reseteamos minijuego
            miniGameId: this.props.filterMiniGameId ?? null
        });
    };

    private handleMiniGameChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        this.setState({
            miniGameId: value ? Number(value) : null
        });
    };

    private handleFieldChange =
        (field: 'name' | 'description' | 'imageUrl' | 'pointsCost' | 'stock') =>
            (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                this.setState({
                    [field]: event.target.value
                } as Pick<IPrizeDialogState, typeof field>);
            };

    private handleToggleActive = (
        event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => {
        this.setState({ isActive: checked });
    };

    private getTournamentNameById(id: number | null): string {
        if (!id) return '';
        const t = this.props.tournaments.find((x) => x.id === id);
        return t?.name || `Torneo ${id}`;
    }

    private getMiniGameNameById(id: number | null): string {
        if (!id) return '';
        const mg = this.props.miniGames.find((x) => x.id === id);
        return mg?.name || `Minijuego ${id}`;
    }

    private async handleSave(): Promise<void> {
        const error = this.validate();
        if (error) {
            this.setState({ error });
            return;
        }

        this.setState({ saving: true, error: null });

        try {
            const { mode, prize } = this.props;
            const {
                tournamentId,
                miniGameId,
                name,
                description,
                imageUrl,
                pointsCost,
                stock,
                isActive
            } = this.state;

            if (!tournamentId) {
                throw new Error('El torneo es obligatorio.');
            }

            const points = Number(pointsCost);
            const stockVal =
                stock.trim() === '' ? null : Number(stock);

            const body = {
                tournamentId,
                name,
                description,
                imageUrl,
                pointsCost: points,
                stock: stockVal,
                isActive
            };

            if (mode === 'create') {
                const response = await Api.post('/api/Prizes', body);
                if (!response.ok) {
                    throw new Error('No se pudo crear el premio.');
                }
            } else {
                if (!prize) {
                    throw new Error('No se ha especificado el premio a editar.');
                }
                const response = await Api.put(`/api/Prizes/${prize.id}`, body);
                if (!response.ok) {
                    throw new Error('No se pudo actualizar el premio.');
                }
            }

            this.props.onSaved();
        } catch (err: any) {
            this.setState({
                saving: false,
                error: err.message || 'Error al guardar el premio.'
            });
        }
    }

    private getMiniGamesForCurrentTournament(): IMiniGameLookup[] {
        const { miniGames } = this.props;
        const { tournamentId } = this.state;

        if (!tournamentId) return [];

        return miniGames.filter((mg) => mg.tournamentId === tournamentId);
    }

    public render(): React.ReactElement<IPrizeDialogProps> {
        const {
            open,
            onClose,
            mode,
            filterTournamentId,
            filterMiniGameId
        } = this.props;
        const {
            tournamentId,
            miniGameId,
            name,
            description,
            imageUrl,
            pointsCost,
            stock,
            isActive,
            saving,
            error
        } = this.state;

        const title = mode === 'create' ? 'Nuevo premio' : 'Editar premio';
        const miniGamesForTournament = this.getMiniGamesForCurrentTournament();

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>{title}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {error && (
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        )}

                        {/* Torneo */}
                        {filterTournamentId !== null ? (
                            <TextField
                                label="Torneo"
                                fullWidth
                                value={this.getTournamentNameById(filterTournamentId)}
                                disabled
                            />
                        ) : (
                            <FormControl fullWidth>
                                <InputLabel id="tournament-label">Torneo</InputLabel>
                                <Select
                                    labelId="tournament-label"
                                    label="Torneo"
                                    value={
                                        tournamentId !== null ? String(tournamentId) : ''
                                    }
                                    onChange={this.handleTournamentChange}
                                >
                                    {this.props.tournaments.map((t) => (
                                        <MenuItem key={t.id} value={t.id.toString()}>
                                            {t.name || `Torneo ${t.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Minijuego (opcional) */}
                        {filterMiniGameId !== null ? (
                            <TextField
                                label="Minijuego"
                                fullWidth
                                value={this.getMiniGameNameById(filterMiniGameId)}
                                disabled
                            />
                        ) : (
                            <FormControl fullWidth>
                                <Select
                                    labelId="minigame-label"
                                    label="Minijuego (opcional)"
                                    value={
                                        miniGameId !== null ? String(miniGameId) : ''
                                    }
                                    onChange={this.handleMiniGameChange}
                                    displayEmpty
                                >
                                    <MenuItem value="">
                                        <em>Sin minijuego</em>
                                    </MenuItem>
                                    {miniGamesForTournament.map((mg) => (
                                        <MenuItem key={mg.id} value={mg.id.toString()}>
                                            {mg.name || `Minijuego ${mg.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <TextField
                            label="Nombre"
                            fullWidth
                            value={name}
                            onChange={this.handleFieldChange('name')}
                            required
                        />

                        <TextField
                            label="Descripción"
                            fullWidth
                            multiline
                            minRows={3}
                            value={description}
                            onChange={this.handleFieldChange('description')}
                        />

                        <TextField
                            label="URL de imagen"
                            fullWidth
                            value={imageUrl}
                            onChange={this.handleFieldChange('imageUrl')}
                            placeholder="https://..."
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Coste en puntos"
                                type="number"
                                fullWidth
                                value={pointsCost}
                                onChange={this.handleFieldChange('pointsCost')}
                                required
                            />
                            <TextField
                                label="Stock (opcional)"
                                type="number"
                                fullWidth
                                value={stock}
                                onChange={this.handleFieldChange('stock')}
                            />
                        </Stack>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isActive}
                                    onChange={this.handleToggleActive}
                                    color="primary"
                                />
                            }
                            label="Premio activo"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => this.handleSave()}
                        disabled={saving}
                    >
                        {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                    <Button onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

// -----------------------------
// Página principal de Prizes
// -----------------------------

export interface IPrizesPageProps { }

export interface IPrizesPageState {
    prizes: IPrizeDto[];
    tournaments: ITournamentLookup[];
    miniGames: IMiniGameLookup[];
    loading: boolean;
    error: string | null;
    searchText: string;
    dialogOpen: boolean;
    dialogMode: 'create' | 'edit';
    editingPrize: IPrizeDto | null;
    filterTournamentId: number | null;
    filterMiniGameId: number | null;
}

export default class PrizesPage extends React.Component<
    IPrizesPageProps,
    IPrizesPageState
> {
    public constructor(props: IPrizesPageProps) {
        super(props);

        const params = new URLSearchParams(window.location.search);
        const tIdStr = params.get('tournamentId');
        const mgIdStr = params.get('miniGameId');

        const filterTournamentId =
            tIdStr && !isNaN(parseInt(tIdStr, 10))
                ? parseInt(tIdStr, 10)
                : null;

        const filterMiniGameId =
            mgIdStr && !isNaN(parseInt(mgIdStr, 10))
                ? parseInt(mgIdStr, 10)
                : null;

        this.state = {
            prizes: [],
            tournaments: [],
            miniGames: [],
            loading: false,
            error: null,
            searchText: '',
            dialogOpen: false,
            dialogMode: 'create',
            editingPrize: null,
            filterTournamentId,
            filterMiniGameId
        };

        this.loadData = this.loadData.bind(this);
        this.handleCreate = this.handleCreate.bind(this);
        this.handleEdit = this.handleEdit.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleDialogClose = this.handleDialogClose.bind(this);
        this.handleDialogSaved = this.handleDialogSaved.bind(this);
        this.renderToolbar = this.renderToolbar.bind(this);
    }

    public componentDidMount(): void {
        this.loadData();
    }

    private async loadData(): Promise<void> {
        this.setState({ loading: true, error: null });

        try {
            const { filterTournamentId, filterMiniGameId } = this.state;

            let prizes: IPrizeDto[] = [];
            let tournaments: ITournamentLookup[] = [];
            let miniGames: IMiniGameLookup[] = [];

            if (filterMiniGameId) {
                const [prResponse, mgResponse] = await Promise.all([
                    Api.get(`/api/Prizes/by-minigame/${filterMiniGameId}`),
                    Api.get(`/api/MiniGames/${filterMiniGameId}`)
                ]);

                if (!prResponse.ok) {
                    throw new Error('No se pudieron cargar los premios.');
                }
                if (!mgResponse.ok) {
                    throw new Error('No se pudo cargar la información del minijuego.');
                }

                prizes = await prResponse.json();
                const mgData: any = await mgResponse.json();
                const tournamentId = mgData.tournamentId;

                const tResponse = await Api.get(
                    `/api/Tournaments/${tournamentId}`
                );
                if (!tResponse.ok) {
                    throw new Error('No se pudo cargar el torneo.');
                }
                const tData: any = await tResponse.json();

                tournaments = [{ id: tData.id, name: tData.name }];
                miniGames = [{ id: mgData.id, name: mgData.name, tournamentId }];

                this.setState({ filterTournamentId: tournamentId });
            } else if (filterTournamentId) {
                const [prResponse, tResponse, mgResponse] = await Promise.all([
                    Api.get(`/api/Prizes/by-tournament/${filterTournamentId}`),
                    Api.get(`/api/Tournaments/${filterTournamentId}`),
                    Api.get(`/api/MiniGames/by-tournament/${filterTournamentId}`)
                ]);

                if (!prResponse.ok) {
                    throw new Error('No se pudieron cargar los premios.');
                }
                if (!tResponse.ok) {
                    throw new Error('No se pudo cargar la información del torneo.');
                }
                if (!mgResponse.ok) {
                    throw new Error('No se pudieron cargar los minijuegos.');
                }

                prizes = await prResponse.json();
                const tData: any = await tResponse.json();
                const mgData: any[] = await mgResponse.json();

                tournaments = [{ id: tData.id, name: tData.name }];
                miniGames = mgData.map((mg) => ({
                    id: mg.id,
                    name: mg.name,
                    tournamentId: mg.tournamentId
                }));
            } else {
                const [prResponse, tResponse, mgResponse] = await Promise.all([
                    Api.get('/api/Prizes'),
                    Api.get('/api/Tournaments'),
                    Api.get('/api/MiniGames')
                ]);

                if (!prResponse.ok) {
                    throw new Error('No se pudieron cargar los premios.');
                }
                if (!tResponse.ok) {
                    throw new Error('No se pudieron cargar los torneos.');
                }
                if (!mgResponse.ok) {
                    throw new Error('No se pudieron cargar los minijuegos.');
                }

                prizes = await prResponse.json();
                const tData: any[] = await tResponse.json();
                const mgData: any[] = await mgResponse.json();

                tournaments = tData.map((t) => ({
                    id: t.id,
                    name: t.name
                }));

                miniGames = mgData.map((mg) => ({
                    id: mg.id,
                    name: mg.name,
                    tournamentId: mg.tournamentId
                }));
            }

            this.setState({
                prizes,
                tournaments,
                miniGames,
                loading: false,
                error: null,
                searchText: ''
            });
        } catch (error: any) {
            this.setState({
                loading: false,
                error: error.message || 'Error al cargar los premios.'
            });
        }
    }

    private handleCreate(): void {
        this.setState({
            dialogOpen: true,
            dialogMode: 'create',
            editingPrize: null
        });
    }

    private handleEdit(prize: IPrizeDto): void {
        this.setState({
            dialogOpen: true,
            dialogMode: 'edit',
            editingPrize: prize
        });
    }

    private async handleDelete(prize: IPrizeDto): Promise<void> {
        const confirmar = window.confirm(
            `¿Seguro que deseas eliminar el premio "${prize.name}"?`
        );
        if (!confirmar) return;

        try {
            const response = await Api.delete(`/api/Prizes/${prize.id}`);
            if (!response.ok) {
                throw new Error('No se pudo eliminar el premio.');
            }

            await this.loadData();
        } catch (error: any) {
            alert(error.message || 'Error al eliminar el premio.');
        }
    }

    private handleSearchChange(
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void {
        this.setState({ searchText: event.target.value });
    }

    private handleDialogClose(): void {
        this.setState({
            dialogOpen: false,
            editingPrize: null
        });
    }

    private async handleDialogSaved(): Promise<void> {
        this.setState({
            dialogOpen: false,
            editingPrize: null
        });
        await this.loadData();
    }

    private getTournamentNameFor(prize: IPrizeDto): string {
        const t = this.state.tournaments.find(
            (x) => x.id === prize.tournamentId
        );
        return t?.name || `Torneo ${prize.tournamentId}`;
    }

    private getMiniGameNameFor(prize: IPrizeDto): string | null {
        if (!prize.miniGameId) return null;
        const mg = this.state.miniGames.find(
            (x) => x.id === prize.miniGameId
        );
        return mg?.name || `Minijuego ${prize.miniGameId}`;
    }

    private matchesSearch(prize: IPrizeDto, term: string): boolean {
        const q = term.trim().toLowerCase();
        if (!q) return true;

        const parts: string[] = [];

        if (prize.name) parts.push(prize.name);
        if (prize.description) parts.push(prize.description);

        const tName = this.getTournamentNameFor(prize);
        if (tName) parts.push(tName);

        const mgName = this.getMiniGameNameFor(prize);
        if (mgName) parts.push(mgName);

        parts.push(String(prize.pointsCost));
        if (prize.stock !== null) parts.push(String(prize.stock));

        parts.push(prize.isActive ? 'activo si sí' : 'inactivo no');

        return parts.some((p) => p.toLowerCase().includes(q));
    }

    private renderToolbar(): React.ReactNode {
        const { searchText } = this.state;

        return (
            <>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={this.handleCreate}
                >
                    Nuevo premio
                </Button>

                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Buscar premio..."
                    value={searchText}
                    onChange={this.handleSearchChange}
                    sx={{
                        ml: 'auto',
                        minWidth: { xs: '100%', sm: 220, md: 260 },
                        mt: { xs: 1, sm: 0 }
                    }}
                />
            </>
        );
    }

    private renderPrizeCard(prize: IPrizeDto): React.ReactNode {
        const tournamentName = this.getTournamentNameFor(prize);
        const miniGameName = this.getMiniGameNameFor(prize);

        return (
            <Grid key={prize.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Card
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 4
                    }}
                >
                    {prize.imageUrl ? (
                        <CardMedia
                            component="img"
                            height="160"
                            image={prize.imageUrl}
                            alt={prize.name || 'Prize image'}
                        />
                    ) : (
                        <Box
                            sx={{
                                height: 160,
                                bgcolor: 'grey.300',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <CardGiftcardIcon
                                sx={{ fontSize: 48, color: 'grey.600' }}
                            />
                        </Box>
                    )}

                    <CardContent sx={{ flexGrow: 1 }}>
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={'5px'} sx={{mb: 2}}>
                            <Typography variant="h6" fontWeight={500} noWrap>
                                {prize.name || 'Sin nombre'}
                            </Typography>
                            <Typography
                                variant="body2"
                                color={prize.isActive ? 'success.main' : 'text.secondary'}
                            >
                                {prize.isActive ? <CheckCircleOutline /> : <CancelOutlined />}
                            </Typography>
                        </Stack>

                        {prize.description && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    mb: 1
                                }}
                            >
                                {prize.description}
                            </Typography>
                        )}

                        <Typography variant="body2" color="text.secondary">
                            Torneo: {tournamentName}
                        </Typography>
                        {miniGameName && (
                            <Typography variant="body2" color="text.secondary">
                                Minijuego: {miniGameName}
                            </Typography>
                        )}

                        <Box sx={{ mt: 1 }}>
                            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={'5px'}>
                                <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} gap={'5px'}>
                                    <Tooltip title="Coste en puntos" arrow>
                                        <MonetizationOnOutlined />
                                    </Tooltip>
                                    <Typography variant="body2">
                                        <strong>{prize.pointsCost}</strong> puntos
                                    </Typography>
                                </Stack>
                                {prize.stock !== null && (
                                    <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} gap={'5px'}>
                                        <Tooltip title="Stock" arrow>
                                            <Inventory2Outlined />
                                        </Tooltip>
                                        <Typography variant="body2">
                                            {prize.stock}
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        </Box>
                    </CardContent>

                    <CardActions
                        sx={{
                            mt: 'auto',
                            justifyContent: 'flex-end',
                            px: 2,
                            pb: 2
                        }}
                    >
                        <Button
                            size="small"
                            onClick={() => this.handleEdit(prize)}
                        >
                            Editar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => this.handleDelete(prize)}
                        >
                            Eliminar
                        </Button>
                    </CardActions>
                </Card>
            </Grid>
        );
    }

    private renderGrid(): React.ReactNode {
        const { prizes, loading, error, searchText } = this.state;

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <Typography color="error" variant="body1">
                    {error}
                </Typography>
            );
        }

        if (!prizes || prizes.length === 0) {
            return (
                <Typography variant="body1" color="text.secondary">
                    No hay premios todavía.
                </Typography>
            );
        }

        const filtered = prizes.filter((p) =>
            this.matchesSearch(p, searchText)
        );

        if (filtered.length === 0) {
            return (
                <Typography variant="body1" color="text.secondary">
                    No hay premios que coincidan con la búsqueda.
                </Typography>
            );
        }

        return (
            <Grid container spacing={3}>
                {filtered.map((p) => this.renderPrizeCard(p))}
            </Grid>
        );
    }

    public render(): React.ReactElement<IPrizesPageProps> {
        const {
            dialogOpen,
            dialogMode,
            editingPrize,
            tournaments,
            miniGames,
            filterTournamentId,
            filterMiniGameId
        } = this.state;

        return (
            <>
                <AdminLayout
                    entityTitle="Premios"
                    entityIcon={<CardGiftcardOutlined sx={{ fontSize: 56 }} />}
                    entityToolbar={this.renderToolbar()}
                >
                    {this.renderGrid()}
                </AdminLayout>

                <PrizeDialog
                    open={dialogOpen}
                    mode={dialogMode}
                    prize={editingPrize}
                    tournaments={tournaments}
                    miniGames={miniGames}
                    filterTournamentId={filterTournamentId}
                    filterMiniGameId={filterMiniGameId}
                    onClose={this.handleDialogClose}
                    onSaved={this.handleDialogSaved}
                />
            </>
        );
    }
}
